import { inject, Injectable } from '@angular/core'
import { IRxStompPublishParams, RxStomp } from '@stomp/rx-stomp'
import { AppConfigService, AuthService, NotificationService, User } from '@dot-it/jml-lib'
import { distinctUntilChanged, filter, map, switchMap, take, withLatestFrom } from 'rxjs/operators'
import { myRxStompConfig } from './rx-stomp.config'
import { BehaviorSubject, merge, Observable, Subscription } from 'rxjs'
import { SETTINGS } from '../../../../shared/settings'
import { GET_CLIENT_ID_AND_MESSAGES_URL, GET_REFRESH_URL, GET_WATCH_MESSAGES_URL } from '../../../../shared/manager'
import { Message } from '../services/notification.constants'

@Injectable({
  providedIn: 'root'
})
export class RxStompService {
  protected watchMessagesSubscription: Subscription = null
  private static rxStomp: RxStomp = new RxStomp()
  private rxStompSubject: BehaviorSubject<RxStomp> = new BehaviorSubject<RxStomp>(undefined)
  private authService: AuthService = inject(AuthService)
  private appConfigService: AppConfigService = inject(AppConfigService)
  private notificationService: NotificationService = inject(NotificationService)
  private loginName = ''

  public init() {
    const userObservable: Observable<[string, string]> = this.getUserObservable()
    userObservable.subscribe(this.initRxStompServiceCallback.bind(this))
    this.initWatchMessagesSubscription()
  }

  public publish(parameters: IRxStompPublishParams) {
    const observable: Observable<RxStomp> = this.rxStompSubject.asObservable().pipe(
      filter((rxStomp) => !!rxStomp),
      take(1)
    )

    observable.subscribe((rxStomp: RxStomp) => this.initRxStompOptions(rxStomp, parameters))
  }

  public watch(destination: string) {
    return this.rxStompSubject.asObservable().pipe(
      filter((rxStomp) => !!rxStomp),
      map((rxStomp) => [rxStomp, destination.replace(SETTINGS.MAIN_REPLACEMENT_VARIABLES.LOGIN_NAME, this.loginName)]),
      switchMap(([rxStomp, resolvedDestination]: [RxStomp, string]) => rxStomp.watch(resolvedDestination))
    )
  }

  public watchRefresh(dtoType: string): Observable<any> {
    return this.watch(GET_REFRESH_URL(dtoType))
  }

  public watchMessages(): Observable<Message> {
    const userMessages = this.watch(GET_WATCH_MESSAGES_URL()).pipe(map((message) => JSON.parse(message.body)))

    const clientSessionMessages: any = this.watch(GET_CLIENT_ID_AND_MESSAGES_URL()).pipe(
      map((message) => JSON.parse(message.body))
    )

    return merge(userMessages, clientSessionMessages)
  }

  private initWatchMessagesSubscription() {
    this.watchMessagesSubscription = this.watchMessages().subscribe((message) =>
      this.notificationService.addNotification(message)
    )
  }

  private getUserObservable() {
    return this.authService.localUser$.pipe(
      map((user: User) => user?.user?.loginName),
      distinctUntilChanged(),
      withLatestFrom(this.appConfigService.getBaseUrl$())
    )
  }

  private initRxStomp() {
    RxStompService.rxStomp = new RxStomp()
    RxStompService.rxStomp.configure(myRxStompConfig)
    RxStompService.rxStomp.activate()
    this.rxStompSubject.next(RxStompService.rxStomp)
  }

  private initRxStompOptions(rxStomp: RxStomp, parameters: IRxStompPublishParams) {
    parameters.headers ??= {}
    parameters.headers[SETTINGS.HEADERS.CLIENT_SESSION_ID] = AuthService.getClientSessionId()
    parameters.headers[SETTINGS.HEADERS.AUTHORIZATION] = this.authService.localUser?.token
    rxStomp.publish(parameters)
  }

  private initRxStompServiceCallback([loginName, baseUrl]) {
    if (!!loginName && !RxStompService.rxStomp.active) {
      this.loginName = loginName
      myRxStompConfig.brokerURL = this.getFormattedWebsocketUrl(baseUrl)
      this.initRxStomp()
    }

    if (!loginName && RxStompService.rxStomp.active) {
      this.loginName = undefined
      this.destroy()
    }
  }

  private getFormattedWebsocketUrl(baseUrl: string): string {
    let formattedUrl: string = baseUrl

    for (const [searchValue, replaceValue] of Object.entries(SETTINGS.WEBSOCKET_URL_REPLACEMENT_OPTIONS)) {
      formattedUrl = formattedUrl.replace(searchValue, replaceValue as string)
    }

    return formattedUrl
  }

  /** destroy means that the ongoing listeners will be notified when the users logs out */
  private destroy() {
    RxStompService.rxStomp.deactivate()
    this.rxStompSubject.next(undefined)
  }
}
